import {
  IMaterialEmissionConfiguration,
  IMaterialEmissionFactor,
  IMaterialEmissionFactorForm,
} from '../modules/packaging-pcf/material-emission-factors/material-emission/types'
import { PRODUCT_TYPE_PACKAGING_MATERIAL } from '../modules/packaging-pcf/material-emission-factors/material-emission/utils'
import { createSlice, PayloadAction } from '@reduxjs/toolkit'

interface IState {
  materialEmissionFactor: IMaterialEmissionFactorForm
  loading: boolean
  error: string
  configuration: IMaterialEmissionConfiguration
}

const initialState: IState = {
  configuration: {
    regions: [],
    countries: [],
    secondaryDataReferenceSourceType: [],
    dataOrigins: [],
    functionalUnits: [],
    flagDisaggregationTypes: [],
    packagingMaterialsTaxonomies: {
      classes: [],
      categories: [],
      functionalUnits: [],
    },
  },
  materialEmissionFactor: {
    code: null,
    state: 0,
    lastPublishedDate: null,
    researcherName: '',
    researcherEmail: '',
    lastUpdatedName: null,
    updated: null,
    companyId: null,

    // Form state only: Category filters the Material name select and is never sent.
    packagingMaterialCategoryId: null,
    productType: PRODUCT_TYPE_PACKAGING_MATERIAL,
    productId: null,
    isRecycled: null,
    // Story 36911: defaults to No, not null, so an untouched record saves as "does not include".
    includesProcessingImpacts: false,
    functionalUnit: null,
    countryId: null,
    regionIdentifierId: null,
    regionStateName: null,

    dataOrigin: null,
    externalSource: null,
    dataOriginComment: null,
    proxyNote: null,

    carbon: null,
    landUse: null,
    biodiversity: null,
    biodiversityPDFYear: null,
    eutrophication: null,
    waterUsage: null,
    waterUsageIncludingWasterScarcity: null,

    flag_DisaggregationType: null,
    flag_L1_LandUseChangeCarbon: null,
    flag_L1_LandManagementCarbon: null,
    flag_L1_LandManagementNonCarbon: null,
    flag_L1_NonLandCarbon: null,
    flag_L1_UnverifiedRemovalsCarbon: null,
    flag_L1_VerifiedRemovalsCarbon: null,

    secondaryDataReferenceSourceType: null,
    dataQualityReliabilityScore: null,
    referencesSamplingYear: null,
    referencesSamplingStartYear: null,
    referenceDataPublicationYear: null,
    referenceRegionIdentifier: null,
    referenceSourcingCountry: null,
    referenceTechnologyType: null,
    referenceTechnologyProxyContribution: null,
    referenceSourcingCountryProxyContribution: null,
    referenceStudy: null,
    numberOfStudies: null,
    numberOfCycles: null,
    dataQualityComment: null,

    isVerified: false,
    verificationComments: null,
    verifiedOn: null,
    verifiedByEmail: null,
    verifiedByName: null,

    impacts: {},
  },
  loading: true,
  error: '',
}

const materialEmissionFactorSlice = createSlice({
  name: 'materialEmissionFactor',
  initialState,
  reducers: {
    materialEmissionFactorRequest(state) {
      state.loading = true
    },
    materialEmissionFactorSuccess(state, action: PayloadAction<IMaterialEmissionFactor>) {
      state.loading = false
      // Category is not on the wire; the form derives it from the material leaf once the
      // taxonomy configuration has loaded.
      state.materialEmissionFactor = {
        ...action.payload,
        packagingMaterialCategoryId: null,
        impacts: action.payload.impacts ?? {},
      }
    },
    materialEmissionFactorFailure(state, action: PayloadAction<string>) {
      state.loading = false
      state.error = action.payload
    },
    configuration(state, action: PayloadAction<IMaterialEmissionConfiguration>) {
      state.configuration = action.payload
    },
    handlePublishStatus(state, action: PayloadAction<number>) {
      state.materialEmissionFactor.state = action.payload
    },
    setDefaultMaterialEmissionFactor: () => initialState,
  },
})

export default materialEmissionFactorSlice.reducer
export const {
  materialEmissionFactorRequest,
  materialEmissionFactorSuccess,
  materialEmissionFactorFailure,
  configuration,
  handlePublishStatus,
  setDefaultMaterialEmissionFactor,
} = materialEmissionFactorSlice.actions
